import { useState } from 'react'
import { DIETARY_LABEL } from '../../dietary'
import { formatCode, formatNames } from '../../guests'
import type { AdminInvite, InvitedPerson, RoomType, RsvpResponse } from '../../types'
import { InviteLink } from './InviteLink'
import { InviteRoom } from './InviteRoom'
import { PersonDietaryEditor } from './PersonDietaryEditor'

interface InviteCardProps {
  invite: AdminInvite
  /** Used to show and change which room this invite holds. */
  roomTypes: readonly RoomType[]
  /** True while this invite's delete is in flight. */
  busy: boolean
  onEdit: () => void
  onDelete: () => void
  /** Called after a guest's requirements are recorded here. */
  onChanged: () => void
  onSessionLost: () => void
}

const RESPONSE_STYLE: Record<RsvpResponse, string> = {
  yes: 'bg-sage/20 text-sage',
  no: 'bg-claret/10 text-claret',
  waiting: 'bg-ink/10 text-ink-soft',
}

const RESPONSE_LABEL: Record<RsvpResponse, string> = {
  yes: 'Yes',
  no: 'No',
  waiting: 'Waiting',
}

/**
 * One invite, read-only, with the code shown the way it is printed.
 *
 * Deleting asks first, and asks inline rather than through window.confirm.
 * Native dialogs are suppressed outright in embedded and hardened browsers -
 * confirm() then just returns false and the delete silently does nothing - and
 * browsers offer to block them after a few uses. Asking in the page always
 * works, and can name exactly what is about to be lost.
 */
export function InviteCard({
  invite,
  roomTypes,
  busy,
  onEdit,
  onDelete,
  onChanged,
  onSessionLost,
}: InviteCardProps) {
  const [confirming, setConfirming] = useState(false)
  const [editingDietary, setEditingDietary] = useState<string | null>(null)
  const names = formatNames(invite.people.map((p) => p.name))

  return (
    <div className="border-ink/10 bg-parchment/50 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-mono text-lg tracking-[0.2em]">{formatCode(invite.code)}</p>
          {!confirming && <InviteLink code={invite.code} />}
        </div>

        {!confirming && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onEdit}
              disabled={busy}
              className="border-ink/20 hover:border-claret hover:text-claret rounded-md border px-3 py-1.5 text-xs tracking-widest uppercase transition-colors disabled:opacity-50"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={busy}
              className="border-claret/30 text-claret hover:bg-claret rounded-md border px-3 py-1.5 text-xs tracking-widest uppercase transition-colors hover:text-white disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* The couple's own note. Hidden while confirming a delete, so the only
          thing on offer then is the decision. */}
      {invite.notes !== '' && !confirming && (
        <p className="border-ink/10 text-ink-soft mt-3 border-l-2 pl-3 text-sm whitespace-pre-line">
          {invite.notes}
        </p>
      )}

      {/* Invite-level, like the code and the note, so it sits here rather than
          against any one guest. */}
      {!confirming && (
        <InviteRoom
          invite={invite}
          types={roomTypes}
          onChanged={onChanged}
          onSessionLost={onSessionLost}
        />
      )}

      <ul className="mt-3 space-y-3">
        {invite.people.map((person) => (
          <li
            key={person.person_id}
            className="border-ink/10 border-t pt-3 first:border-t-0 first:pt-0"
          >
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>{person.name}</span>
              <span className="bg-ink/5 text-ink-soft rounded-full px-2 py-0.5 text-[0.65rem] tracking-widest uppercase">
                {person.invite_type === 'evening' ? 'Evening' : 'All day'}
              </span>
              {person.is_child && (
                <span className="bg-ink/5 text-ink-soft rounded-full px-2 py-0.5 text-[0.65rem] tracking-widest uppercase">
                  Child
                </span>
              )}
              <span
                className={`rounded-full px-2 py-0.5 text-[0.65rem] tracking-widest uppercase ${RESPONSE_STYLE[person.response]}`}
              >
                {RESPONSE_LABEL[person.response]}
              </span>
              {/* The server's verdict, not ours - it is what the guest page
                  goes by too, so the two can never disagree. */}
              {person.response === 'yes' && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[0.65rem] tracking-widest uppercase ${
                    person.dietary_complete ? 'bg-sage/20 text-sage' : 'bg-ink/10 text-ink-soft'
                  }`}
                >
                  {person.dietary_complete ? 'Food done' : 'Food outstanding'}
                </span>
              )}
              {person.response === 'yes' && editingDietary !== person.person_id && (
                <button
                  type="button"
                  onClick={() => setEditingDietary(person.person_id)}
                  // Named per guest: "Edit food" repeats down the list and says
                  // nothing on its own about which row it belongs to.
                  aria-label={`Edit food for ${person.name}`}
                  className="border-ink/20 hover:border-claret hover:text-claret ml-auto shrink-0 rounded-md border px-2 py-1 text-[0.65rem] tracking-widest uppercase transition-colors"
                >
                  Edit food
                </button>
              )}
            </div>

            {editingDietary !== person.person_id && <FoodSummary person={person} />}

            {editingDietary === person.person_id && (
              <PersonDietaryEditor
                person={person}
                onSaved={() => {
                  setEditingDietary(null)
                  onChanged()
                }}
                onCancel={() => setEditingDietary(null)}
                onSessionLost={onSessionLost}
              />
            )}
          </li>
        ))}
      </ul>

      {confirming && (
        <div
          role="alertdialog"
          aria-label={`Confirm deleting invite ${formatCode(invite.code)}`}
          className="border-claret/30 bg-claret/5 mt-4 rounded-md border p-3"
        >
          <p className="text-sm">
            Delete this invite for <strong>{names}</strong>? Their replies go with it, and this
            cannot be undone.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              autoFocus
              className="bg-claret hover:bg-claret-dark rounded-md px-4 py-1.5 text-xs tracking-widest text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Yes, delete'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="border-ink/20 hover:border-claret rounded-md border px-4 py-1.5 text-xs tracking-widest uppercase transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** What one guest can eat, as the kitchen needs it read out. */
function FoodSummary({ person }: { person: InvitedPerson }) {
  if (person.dietary.length === 0) {
    // Only worth remarking on for somebody who is coming - there is nothing
    // outstanding from a guest who has not replied, or who cannot make it.
    if (person.response !== 'yes') return null
    return (
      <p className="text-ink-soft mt-1 text-xs">
        {person.dietary_submitted_at === null
          ? 'Not heard from them about food.'
          : 'No requirements.'}
      </p>
    )
  }

  return (
    <p className="text-ink-soft mt-1 text-xs">
      {person.dietary.map((r) => DIETARY_LABEL[r]).join(', ')}
    </p>
  )
}
